import { useAuth } from "@clerk/clerk-react";
import { Box, TextField } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  BentoSearchFilter,
  OrganizationProfile,
  ProfileType,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import StyledAutocomplete from "components/Styled/Autocomplete";
import MultiChipSelect from "components/Styled/MultiChipSelect";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { titleCase } from "utils/string";

interface IProps {
  profile: OrganizationProfile | null;
  setProfile: Dispatch<SetStateAction<OrganizationProfile | null>>;
  includedBentoSearchFilterIds: number[];
  excludedBentoSearchFilterIds: number[];
  setIncludedBentoSearchFilterIds: Dispatch<SetStateAction<number[]>>;
  setExcludedBentoSearchFilterIds: Dispatch<SetStateAction<number[]>>;
  setIsEdited: Dispatch<SetStateAction<boolean>>;
  activeQuestionIndex?: number;
  setActiveQuestionIndex?: Dispatch<SetStateAction<number>>;
  error?: string;
}

export default function OnboardingSettings({
  // vaSearchFilters,
  profile,
  setProfile,
  includedBentoSearchFilterIds,
  excludedBentoSearchFilterIds,
  setIncludedBentoSearchFilterIds,
  setExcludedBentoSearchFilterIds,
  setIsEdited,
  activeQuestionIndex,
  setActiveQuestionIndex,
  error,
}: IProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const isControlled =
    activeQuestionIndex !== undefined &&
    isPage(routes.aboutSelfServeOnboarding);

  const [vaSearchFilters, setVaSearchFilters] = useState<BentoSearchFilter[]>(
    [],
  );
  const includeSearchFilters = vaSearchFilters.filter(
    (f) => f.enableForVaInclude,
  );

  const excludeSearchFilters = vaSearchFilters.filter(
    (f) => f.enableForVaExclude,
  );

  const fetchFilters = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-search-filters?for_va=true`,
        "GET",
      );
      setVaSearchFilters(res.bentoSearchFilters);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 4 }}>
      <Box>
        <StyledAutocomplete
          required
          label="Who are you?"
          multiple
          options={Object.values(ProfileType)}
          value={profile?.profileTypes || []}
          onChange={(_: any, newValue: ProfileType[]) => {
            setIsEdited(true);
            setProfile((p) => {
              if (!p) return p;
              const copy = { ...p };
              copy.profileTypes = newValue;
              return copy;
            });
          }}
          completed={profile?.profileTypes && profile?.profileTypes?.length > 0}
          isActive={isControlled ? activeQuestionIndex === 2 : undefined}
          error={activeQuestionIndex === 2 && error ? error : ""}
        />
        {profile?.profileTypes?.includes(ProfileType.other) && (
          <TextField
            placeholder="Please specify"
            value={profile?.otherType || ""}
            onChange={(e: CustomEvent) => {
              const { value } = e.target;
              setIsEdited(true);
              setProfile((p) => {
                if (!p) return p;
                const copy = { ...p };
                copy.otherType = value;
                return copy;
              });
            }}
            variant="standard"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      <MultiChipSelect
        label="Tell us about you and the content you create"
        description="Select all that apply — this helps us tailor opportunities that fit you best."
        options={includeSearchFilters.map(
          (searchFilter: BentoSearchFilter) => ({
            id: searchFilter.id,
            label: searchFilter.vaDisplayName || titleCase(searchFilter.name),
          }),
        )}
        completed={includedBentoSearchFilterIds?.length > 0}
        isOpen={isControlled ? activeQuestionIndex === 3 : undefined}
        onRequestOpen={
          isControlled ? () => setActiveQuestionIndex!(3) : undefined
        }
        onCollapse={isControlled ? () => setActiveQuestionIndex!(4) : undefined}
        onClickOption={(option) => {
          setIsEdited(true);
          setIncludedBentoSearchFilterIds((prev) => {
            if (prev.includes(option.id)) {
              return prev.filter((item) => item !== option.id);
            } else {
              return [...prev, option.id];
            }
          });
        }}
        selectedOptionsIds={includedBentoSearchFilterIds}
      />

      <MultiChipSelect
        selectedChipColor="secondary"
        label="Categories you'd prefer to avoid"
        description="Select all that apply."
        options={excludeSearchFilters.map(
          (searchFilter: BentoSearchFilter) => ({
            id: searchFilter.id,
            label: searchFilter.vaDisplayName || titleCase(searchFilter.name),
          }),
        )}
        completed={excludedBentoSearchFilterIds?.length > 0}
        isOpen={isControlled ? activeQuestionIndex === 4 : undefined}
        onRequestOpen={
          isControlled ? () => setActiveQuestionIndex!(4) : undefined
        }
        onCollapse={
          isControlled ? () => setActiveQuestionIndex!(-1) : undefined
        }
        onClickOption={(option) => {
          setIsEdited(true);
          setExcludedBentoSearchFilterIds((prev) => {
            if (prev.includes(option.id)) {
              return prev.filter((item) => item !== option.id);
            } else {
              return [...prev, option.id];
            }
          });
        }}
        selectedOptionsIds={excludedBentoSearchFilterIds}
      />
    </Box>
  );
}
